import { BankDetail } from '@sr-sdks/api-sdk-axios';
import { useMemo } from 'react';
import { FEATURE_FLAGS } from '../constant/featureFlags';
import { bankDetailsService } from '../reducers/payments/bankDetailSlice';
import { useTypedSelector } from '../stateStore';
import { isSuccessState } from '../utils/helpers/common';
import { useFeatureFlag } from './useFeatureFlag';

/**
 * When multi-bank is disabled, only the default account should be shown in lists.
 * Expects `bankDetails` in `isDefault:desc` order from the API.
 */
function selectVisibleBankDetails(
  bankDetails: BankDetail[],
  isMultiBankAccountEnabled: boolean,
): BankDetail[] {
  if (isMultiBankAccountEnabled || bankDetails.length <= 1) {
    return bankDetails;
  }

  const defaultAccount = bankDetails.find((detail) => detail.isDefault);

  return [defaultAccount ?? bankDetails[0]];
}

/**
 * Resolves multi-bank feature flag and returns bank details visible in list UIs.
 */
export function useVisibleBankDetails() {
  const bankDetails = useTypedSelector(
    bankDetailsService.selectors.bankDetails,
  );
  const bankDetailsLoadingState = useTypedSelector(
    bankDetailsService.selectors.isLoading,
  );
  const [isMultiBankAccountEnabled, multiBankFeaturesQuery] = useFeatureFlag(
    FEATURE_FLAGS.MULTI_BANK_ACCOUNT,
  );

  // The flag has three states, not two. `data` is the only proof the value is
  // known, so a query that has finished without it is *unavailable* rather
  // than merely "not enabled". `isFetched` is sticky once the query has
  // settled either way, which keeps `unavailable` a terminal state instead of
  // one that flips back to loading every time another observer mounts.
  const isMultiBankFlagResolved = multiBankFeaturesQuery.data !== undefined;
  const isMultiBankFlagUnavailable =
    !isMultiBankFlagResolved && multiBankFeaturesQuery.isFetched;
  const isMultiBankFlagLoading =
    !isMultiBankFlagResolved && !isMultiBankFlagUnavailable;

  const visibleBankDetails = useMemo(() => {
    // Fail closed: an unknown flag never grants. While the flag is in flight,
    // or after the request failed outright, we behave as though multi-bank is
    // off and expose only the default account, so no caller can leak an
    // account the flag exists to hide. Callers that must not paint either
    // variant before the flag is known read `isMultiBankFlagLoading` /
    // `isMultiBankFlagUnavailable` and render neither. Gating on `data`
    // rather than `isSuccess` keeps a cached value authoritative when a
    // background refetch fails.
    return selectVisibleBankDetails(
      bankDetails,
      isMultiBankFlagResolved && isMultiBankAccountEnabled,
    );
  }, [bankDetails, isMultiBankAccountEnabled, isMultiBankFlagResolved]);

  // Single source of truth for "may the user add a bank account" -- this had
  // drifted into two inline copies (WorkspaceSettingsLayout, NewBankDetails)
  // before it grew a third would-be copy in BankDetailSideOverlay. Unknown
  // never grants: an unresolved flag, or a flag-off state with an unconfirmed
  // account count, both yield `false`.
  const canAddBankAccount =
    isMultiBankFlagResolved &&
    (isMultiBankAccountEnabled ||
      (isSuccessState(bankDetailsLoadingState) && bankDetails.length === 0));

  return {
    bankDetails,
    canAddBankAccount,
    isMultiBankAccountEnabled,
    isMultiBankFlagLoading,
    isMultiBankFlagUnavailable,
    visibleBankDetails,
  };
}
